/**
 * MediaPipe-based teleoperation device (TypeScript version).
 * Extracts human SEW (Shoulder, Elbow, Wrist) coordinates and wrist poses from camera stream.
 * Works in browser using @mediapipe/pose and @mediapipe/hands.
 *
 * MediaPipe 3D Coordinate System (right-handed):
 *   - Origin: Midpoint of subject's hips (world landmarks reference)
 *   - X-axis: Positive points to subject's right
 *   - Y-axis: Positive points downwards
 *   - Z-axis: Positive points away from camera (deeper into scene)
 *
 * Body-centric coordinate frame (used for robot control):
 *   - Origin: Shoulder center (for upper body tracking)
 *   - Y-axis: Right to left (shoulder line)
 *   - Z-axis: Up direction (opposite of torso vector)
 *   - X-axis: Forward direction (cross product Y × Z)
 *
 * Robot body-centric frame (final output):
 *     +X - front
 *     +Y - left
 *     +Z - up
 * For both hands/wrists, when in zero config (arm by body side), follows the same coordinates as above.
 *
 * Author: Chuizheng Kong and Yunho Cho
 * Date created: 2025-09-12
 */

import {
  PoseLandmarker,
  // HandLandmarker, // TEMPORARILY DISABLED - hand tracking disabled
  FilesetResolver,
  type PoseLandmarkerResult,
  // type HandLandmarkerResult // TEMPORARILY DISABLED - hand tracking disabled
} from '@mediapipe/tasks-vision';
import { Matrix, SingularValueDecomposition } from 'ml-matrix';
import { mat3, vec3 } from 'gl-matrix';
import { recordExternalIkSolveDuration } from '$lib/stores/ik';
import { loadSewMimicModule, type SewMimicModule } from '$lib/sew-mimic/runtime';

// MediaPipe Pose landmark indices
const POSE_LANDMARKS = {
  NOSE: 0,
  LEFT_EYE_INNER: 1,
  LEFT_EYE: 2,
  LEFT_EYE_OUTER: 3,
  RIGHT_EYE_INNER: 4,
  RIGHT_EYE: 5,
  RIGHT_EYE_OUTER: 6,
  LEFT_EAR: 7,
  RIGHT_EAR: 8,
  MOUTH_LEFT: 9,
  MOUTH_RIGHT: 10,
  LEFT_SHOULDER: 11,
  RIGHT_SHOULDER: 12,
  LEFT_ELBOW: 13,
  RIGHT_ELBOW: 14,
  LEFT_WRIST: 15,
  RIGHT_WRIST: 16,
  LEFT_PINKY: 17,
  RIGHT_PINKY: 18,
  LEFT_INDEX: 19,
  RIGHT_INDEX: 20,
  LEFT_THUMB: 21,
  RIGHT_THUMB: 22,
  LEFT_HIP: 23,
  RIGHT_HIP: 24,
  LEFT_KNEE: 25,
  RIGHT_KNEE: 26,
  LEFT_ANKLE: 27,
  RIGHT_ANKLE: 28,
  LEFT_HEEL: 29,
  RIGHT_HEEL: 30,
  LEFT_FOOT_INDEX: 31,
  RIGHT_FOOT_INDEX: 32
} as const;

// MediaPipe Hand landmark indices
// TEMPORARILY DISABLED - hand tracking disabled
// const HAND_LANDMARKS = {
//   WRIST: 0,
//   THUMB_CMC: 1,
//   THUMB_MCP: 2,
//   THUMB_IP: 3,
//   THUMB_TIP: 4,
//   INDEX_FINGER_MCP: 5,
//   INDEX_FINGER_PIP: 6,
//   INDEX_FINGER_DIP: 7,
//   INDEX_FINGER_TIP: 8,
//   MIDDLE_FINGER_MCP: 9,
//   MIDDLE_FINGER_PIP: 10,
//   MIDDLE_FINGER_DIP: 11,
//   MIDDLE_FINGER_TIP: 12,
//   RING_FINGER_MCP: 13,
//   RING_FINGER_PIP: 14,
//   RING_FINGER_DIP: 15,
//   RING_FINGER_TIP: 16,
//   PINKY_MCP: 17,
//   PINKY_PIP: 18,
//   PINKY_DIP: 19,
//   PINKY_TIP: 20
// } as const;

interface SEWPose {
  S: vec3 | null;
  E: vec3 | null;
  W: vec3 | null;
}

interface SEWPoses {
  left: SEWPose;
  right: SEWPose;
  R_world_body?: mat3 | null;
}

interface WristPoses {
  left: mat3 | null;
  right: mat3 | null;
}

interface HandData {
  landmarks: { [key: string]: vec3 } | null;
  confidence: number;
}

interface HandPoses {
  left: HandData;
  right: HandData;
}

export interface ControllerState {
  sew_poses: SEWPoses;
  wrist_poses: WristPoses;
  torso_angles: vec3 | null;
  engaged: boolean;
  g1_solution?: {
    left_arm: number[];
    right_arm: number[];
    torso?: number[] | null;
    control_commands: number[];
    metadata?: unknown;
  } | null;
}

interface BodyFrame {
  origin: vec3;
  x_axis: vec3;
  y_axis: vec3;
  z_axis: vec3;
  R_world_body: mat3;
}

export class MediaPipeTeleopDevice {
  private poseLandmarker: PoseLandmarker | undefined;
  private sewMimicModule: SewMimicModule | null = null;
  // private handLandmarker: HandLandmarker | undefined; // TEMPORARILY DISABLED - hand tracking disabled
  private mirrorActions: boolean;
  private debug: boolean;
  private engaged: boolean;

  // State tracking
  private humanSewPoses!: SEWPoses;
  private humanWristPoses!: WristPoses;
  private humanHandPoses!: HandPoses;
  private controllerState: ControllerState | null;
  private latestPoseResults: PoseLandmarkerResult | null = null;

  // Pre-allocated vectors and matrices for memory efficiency
  private _tempVec3_1: vec3 = vec3.create();
  private _tempVec3_2: vec3 = vec3.create();
  private _tempVec3_3: vec3 = vec3.create();
  private _tempVec3_4: vec3 = vec3.create();
  private _tempVec3_5: vec3 = vec3.create();
  private _tempMat3_1: mat3 = mat3.create();
  private _tempMat3_2: mat3 = mat3.create();

  // Constant rotation matrix
  // private readonly R_STD_CAM: mat3 = mat3.fromValues(
  //   0, 0, -1,
  //   1, 0, 0,
  //   0, -1, 0
  // );

  // private readonly R_STD_CAM: mat3 = mat3.fromValues(
  //   1, 0, 0,
  //   0, 1, 0,
  //   0, 0, 1
  // );

  // in ts, matrix are created in column-major order
  private readonly R_STD_CAM: mat3 = mat3.fromValues(
    0, 1, 0,  // col 1 
    0, 0, -1,  // col 2
    -1, 0, 0
  );

  // Standard camera rotation matrix

  constructor(options: {
    debug?: boolean;
    mirrorActions?: boolean;
  } = {}) {
    this.debug = options.debug ?? false;
    this.mirrorActions = options.mirrorActions ?? false;
    this.engaged = false;
    this.controllerState = null;

    this._resetInternalState();
    // this._displayControls();
    this._initializeMediaPipe();
  }

  private async _initializeMediaPipe(): Promise<void> {
    try {
      this.sewMimicModule = await loadSewMimicModule();

      const vision = await FilesetResolver.forVisionTasks('/wasm');

      // Initialize pose landmarker
      this.poseLandmarker = await PoseLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task',
          // modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_full/float16/latest/pose_landmarker_full.task',
          // modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_heavy/float16/latest/pose_landmarker_heavy.task',
          delegate: 'GPU'
        },
        runningMode: 'VIDEO',
        numPoses: 1,
        minPoseDetectionConfidence: 0.5,
        minPosePresenceConfidence: 0.5,
        minTrackingConfidence: 0.5
      });

      // Initialize hand landmarker
      // this.handLandmarker = await HandLandmarker.createFromOptions(vision, {
      //   baseOptions: {
      //     modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task',
      //     delegate: 'GPU'
      //   },
      //   runningMode: 'VIDEO',
      //   numHands: 2,
      //   minHandDetectionConfidence: 0.5,
      //   minHandPresenceConfidence: 0.5,
      //   minTrackingConfidence: 0.5
      // });

    } catch (error) {
      if (this.debug) {
        console.error('Error initializing MediaPipe:', error);
      }
    }
  }

  private _displayControls(): void {
    console.log("MediaPipe Teleoperation Device Controls:");
    console.log("- Raise both arms to shoulder height: Start pose tracking");
    console.log("- Lower arms: Stop pose tracking");
  }

  private _resetInternalState(): void {
    this.humanSewPoses = {
      left: { S: null, E: null, W: null },
      right: { S: null, E: null, W: null },
      R_world_body: null
    };
    this.humanWristPoses = {
      left: null,
      right: null
    };
    this.humanHandPoses = {
      left: { landmarks: null, confidence: 0.0 },
      right: { landmarks: null, confidence: 0.0 }
    };
    this.engaged = false;
  }

  async processFrame(videoElement: HTMLVideoElement): Promise<void> {
    if (!this.poseLandmarker || !this.sewMimicModule) {
      return; // Models not ready yet
    }

    const startTimeMs = performance.now();

    // Process pose detection
    const poseResults = this.poseLandmarker.detectForVideo(videoElement, startTimeMs);
    this.latestPoseResults = poseResults;

    this._processFrameWithRust(poseResults);

    // Process hand detection - TEMPORARILY DISABLED
    // const handResults = this.handLandmarker.detectForVideo(videoElement, startTimeMs);
    // this._processHandResults(handResults);
  }

  private _processFrameWithRust(poseResults: PoseLandmarkerResult): void {
    if (!this.sewMimicModule) {
      return;
    }

    try {
      const frameInput = {
        pose_world_landmarks: poseResults.worldLandmarks?.[0]?.map((lm) => ({ x: lm.x, y: lm.y, z: lm.z })) ?? [],
        pose_visibility: poseResults.landmarks?.[0]?.map((lm) => lm.visibility ?? 0.0) ?? [],
        hands: [],
        solve_ik: true,
        mirror_actions: this.mirrorActions
      };

      const solveStart = performance.now();
      const output = (this.sewMimicModule as unknown as {
        g1_process_mediapipe_frame: (frame: unknown) => unknown;
      }).g1_process_mediapipe_frame(frameInput) as {
        controller_state?: {
          sew_poses?: {
            left?: { S?: number[] | null; E?: number[] | null; W?: number[] | null };
            right?: { S?: number[] | null; E?: number[] | null; W?: number[] | null };
            R_world_body?: number[] | null;
          };
          torso_angles?: number[] | null;
          engaged?: boolean;
        };
        g1_solution?: {
          left_arm: number[];
          right_arm: number[];
          torso?: number[] | null;
          control_commands: number[];
          metadata?: unknown;
        } | null;
      };

      recordExternalIkSolveDuration(performance.now() - solveStart);

      const state = output.controller_state;
      if (!state?.sew_poses) {
        return;
      }

      const toVec3 = (v?: number[] | null): vec3 | null => {
        if (!v || v.length < 3) return null;
        return vec3.fromValues(v[0], v[1], v[2]);
      };

      const toMat3 = (m?: number[] | null): mat3 | null => {
        if (!m || m.length < 9) return null;
        return mat3.fromValues(m[0], m[1], m[2], m[3], m[4], m[5], m[6], m[7], m[8]);
      };

      this.humanSewPoses = {
        left: {
          S: toVec3(state.sew_poses.left?.S),
          E: toVec3(state.sew_poses.left?.E),
          W: toVec3(state.sew_poses.left?.W)
        },
        right: {
          S: toVec3(state.sew_poses.right?.S),
          E: toVec3(state.sew_poses.right?.E),
          W: toVec3(state.sew_poses.right?.W)
        },
        R_world_body: toMat3(state.sew_poses.R_world_body)
      };

      this.engaged = Boolean(state.engaged);

      this.controllerState = {
        sew_poses: this._cloneSewPoses(this.humanSewPoses),
        wrist_poses: { left: null, right: null },
        torso_angles: toVec3(state.torso_angles),
        engaged: this.engaged,
        g1_solution: output.g1_solution ?? null
      };
    } catch (error) {
      if (this.debug) {
        console.error('Error processing pose frame in Rust WASM pipeline:', error);
      }
      this._processPoseResults(poseResults);
    }
  }

  private _processPoseResults(results: PoseLandmarkerResult): void {
    // Store latest results for drawing
    this.latestPoseResults = results;

    try {
      if (results.landmarks && results.worldLandmarks && results.landmarks.length > 0) {
        // Get body-centric coordinates
        const bodyCentricCoords = this._getBodyCentricCoordinates(results);

        if (bodyCentricCoords) {
          // Check visibility using normalized landmarks
          const landmarks = results.worldLandmarks[0]; // First pose
          const leftShoulder = landmarks[POSE_LANDMARKS.LEFT_SHOULDER];
          const leftElbow = landmarks[POSE_LANDMARKS.LEFT_ELBOW];
          const leftWrist = landmarks[POSE_LANDMARKS.LEFT_WRIST];

          const rightShoulder = landmarks[POSE_LANDMARKS.RIGHT_SHOULDER];
          const rightElbow = landmarks[POSE_LANDMARKS.RIGHT_ELBOW];
          const rightWrist = landmarks[POSE_LANDMARKS.RIGHT_WRIST];

          // Check visibility (same threshold as Python version)
          const leftSewValid = [leftShoulder, leftElbow, leftWrist].every(lm => lm.visibility && lm.visibility > 0.05);
          const rightSewValid = [rightShoulder, rightElbow, rightWrist].every(lm => lm.visibility && lm.visibility > 0.05);

          // Update SEW poses only if landmarks are visible
          if (leftSewValid && bodyCentricCoords.left) {
            this.humanSewPoses.left = bodyCentricCoords.left;
          } else {
            this.humanSewPoses.left = { S: null, E: null, W: null };
          }

          if (rightSewValid && bodyCentricCoords.right) {
            this.humanSewPoses.right = bodyCentricCoords.right;
          } else {
            this.humanSewPoses.right = { S: null, E: null, W: null };
          }

          if (leftSewValid && rightSewValid && bodyCentricCoords.body_frame) {
            this.humanSewPoses.R_world_body = bodyCentricCoords.body_frame.R_world_body;
          } else {
            this.humanSewPoses.R_world_body = null;
          }

          this._checkEngagement();
        } else {
          // No body-centric coordinates - reset poses
          this.humanSewPoses.left = { S: null, E: null, W: null };
          this.humanSewPoses.right = { S: null, E: null, W: null };
          this.humanSewPoses.R_world_body = null;
          this._checkEngagement();
        }
      } else {
        // No pose landmarks - reset poses
        this.humanSewPoses.left = { S: null, E: null, W: null };
        this.humanSewPoses.right = { S: null, E: null, W: null };
        this.humanSewPoses.R_world_body = null;
        this._checkEngagement();
      }

      // Update controller state
      const torsoAngles: vec3 | null = null;
      // Removed JS-side torso angle calculation in favor of Rust implementation downstream
      // if (this.humanSewPoses.R_world_body) {
      //   torsoAngles = this._computeTorsoAngles(this.humanSewPoses.R_world_body);
      // }

      // Compute wrist poses
      const leftWristPose = this._computeWristRotationFromHand('left', results, this.humanSewPoses);
      // if (leftWristPose) {
      //   console.log("Left Wrist Rotation:", leftWristPose);
      // }
      const rightWristPose = this._computeWristRotationFromHand('right', results, this.humanSewPoses);
      
      this.controllerState = {
        sew_poses: this._cloneSewPoses(this.humanSewPoses),
        wrist_poses: { left: leftWristPose, right: rightWristPose },
        torso_angles: torsoAngles,
        engaged: this.engaged
      };

    } catch (error) {
      if (this.debug) {
        console.error("Error processing pose landmarks:", error);
      }
    }
  }


  private _transformToBodyFrame(landmark: { x: number; y: number; z: number }): vec3 {
    // Reuse _tempVec3_2 for worldPos
    vec3.set(this._tempVec3_2, landmark.x, landmark.y, landmark.z);

    // Reuse _tempVec3_4 for relativePos
    // _tempVec3_3 holds bodyOrigin
    vec3.subtract(this._tempVec3_4, this._tempVec3_2, this._tempVec3_3);

    // Apply transformation: rotation_matrix.T @ relative_pos
    // _tempMat3_1 holds rotationMatrix
    const robotCoords = vec3.create();
    vec3.transformMat3(robotCoords, this._tempVec3_4, this._tempMat3_2);

    return robotCoords;
  }

  private _getBodyCentricCoordinates(poseResults: PoseLandmarkerResult): {
    left: SEWPose;
    right: SEWPose;
    body_frame: BodyFrame;
  } | null {
    if (!poseResults.worldLandmarks || poseResults.worldLandmarks.length === 0) {
      return null;
    }

    const landmarks3d = poseResults.worldLandmarks[0]; // First pose

    // Get key body landmarks
    const leftShoulder = landmarks3d[POSE_LANDMARKS.LEFT_SHOULDER];
    const rightShoulder = landmarks3d[POSE_LANDMARKS.RIGHT_SHOULDER];
    const leftHip = landmarks3d[POSE_LANDMARKS.LEFT_HIP];
    const rightHip = landmarks3d[POSE_LANDMARKS.RIGHT_HIP];

    // Calculate body center (midpoint between shoulders and hips)
    // Use _tempVec3_1 for shoulderCenter
    vec3.set(this._tempVec3_1,
      (leftShoulder.x + rightShoulder.x) / 2,
      (leftShoulder.y + rightShoulder.y) / 2,
      (leftShoulder.z + rightShoulder.z) / 2
    );

    // Use _tempVec3_2 for hipCenter
    vec3.set(this._tempVec3_2,
      (leftHip.x + rightHip.x) / 2,
      (leftHip.y + rightHip.y) / 2,
      (leftHip.z + rightHip.z) / 2
    );

    // Use _tempVec3_3 for bodyOrigin (clone of shoulderCenter)
    vec3.copy(this._tempVec3_3, this._tempVec3_1);

    // Create body-centric coordinate frame
    // Based on user observations, we need to map MediaPipe coordinates correctly:
    // MediaPipe: X+ = subject's right, Y+ = down, Z+ = away from camera
    // Robot: X+ = front, Y+ = left, Z+ = up

    // Robot Y-axis (left): MediaPipe X+ direction (subject's right to left)
    // Use _tempVec3_4 for robotYAxis
    vec3.set(this._tempVec3_4,
      leftShoulder.x - rightShoulder.x,
      leftShoulder.y - rightShoulder.y,
      leftShoulder.z - rightShoulder.z
    );
    vec3.normalize(this._tempVec3_4, this._tempVec3_4);

    // Robot Z-axis (up): opposite of MediaPipe Y+ (up is negative of down)
    // torsoVector = hipCenter - shoulderCenter
    // Use _tempVec3_5 for torsoVector
    vec3.subtract(this._tempVec3_5, this._tempVec3_2, this._tempVec3_1);
    // robotZAxis = -torsoVector
    // Reuse _tempVec3_5 for robotZAxis
    vec3.negate(this._tempVec3_5, this._tempVec3_5);
    vec3.normalize(this._tempVec3_5, this._tempVec3_5);

    // Robot X-axis (front): cross product Y × Z
    // Reuse _tempVec3_1 for robotXAxis (shoulderCenter no longer needed)
    vec3.cross(this._tempVec3_1, this._tempVec3_4, this._tempVec3_5);
    vec3.normalize(this._tempVec3_1, this._tempVec3_1);

    // Create rotation matrix: [X_robot, Y_robot, Z_robot] (columns are robot axes)
    // We construct the Transpose directly to get World->Body transformation
    // The matrix constructed here is [X0 X1 X2; Y0 Y1 Y2; Z0 Z1 Z2]
    // Use _tempMat3_1
    // mat3.set(this._tempMat3_1,
    //   this._tempVec3_1[0], this._tempVec3_4[0], this._tempVec3_5[0],
    //   this._tempVec3_1[1], this._tempVec3_4[1], this._tempVec3_5[1],
    //   this._tempVec3_1[2], this._tempVec3_4[2], this._tempVec3_5[2]
    // );

    mat3.set(this._tempMat3_1,
      this._tempVec3_1[0], this._tempVec3_1[1], this._tempVec3_1[2],
      this._tempVec3_4[0], this._tempVec3_4[1], this._tempVec3_4[2],
      this._tempVec3_5[0], this._tempVec3_5[1], this._tempVec3_5[2]
    );

    mat3.transpose(this._tempMat3_2, this._tempMat3_1); // _tempMat3_2 is now rotationMatrix.T

    // Create transposed rotation matrix for transformation (equivalent to rotation_matrix.T in Python)
    // const rotationMatrixT = mat3.transpose(mat3.create(), rotationMatrix);
    // turns out that rotationMatrix is R_world_body.T already.

    // Calculate R_world_body for robot control (equivalent to Python implementation)
    // R_std_cam matrix from Python: [[0, 1, 0], [0, 0, -1], [-1, 0, 0]]
    // gl-matrix is column-major, so we pass columns:
    // Col 0: [0, 0, -1]
    // Col 1: [1, 0, 0]
    // Col 2: [0, -1, 0]
    // This is now this.R_STD_CAM

    // Reconstruct the rotation matrix with columns as axes (equivalent to Python's rotation_matrix)
    // const R_body_orientation = mat3.fromValues(
    //   robotXAxis[0], robotXAxis[1], robotXAxis[2],
    //   robotYAxis[0], robotYAxis[1], robotYAxis[2],
    //   robotZAxis[0], robotZAxis[1], robotZAxis[2]
    // );

    const R_world_body_corrected = mat3.create();
    mat3.multiply(R_world_body_corrected, this.R_STD_CAM, this._tempMat3_1);
    // mat3.multiply(R_world_body_corrected, this._tempMat3_1, this.R_STD_CAM);

    // Extract SEW coordinates in body-centric frame
    const sewCoordinates = {
      left: {
        S: this._transformToBodyFrame(landmarks3d[POSE_LANDMARKS.LEFT_SHOULDER]),
        E: this._transformToBodyFrame(landmarks3d[POSE_LANDMARKS.LEFT_ELBOW]),
        W: this._transformToBodyFrame(landmarks3d[POSE_LANDMARKS.LEFT_WRIST])
      },
      right: {
        S: this._transformToBodyFrame(landmarks3d[POSE_LANDMARKS.RIGHT_SHOULDER]),
        E: this._transformToBodyFrame(landmarks3d[POSE_LANDMARKS.RIGHT_ELBOW]),
        W: this._transformToBodyFrame(landmarks3d[POSE_LANDMARKS.RIGHT_WRIST])
      },
      body_frame: {
        origin: vec3.clone(this._tempVec3_3), // Clone to persist
        x_axis: vec3.clone(this._tempVec3_1), // Clone to persist
        y_axis: vec3.clone(this._tempVec3_4), // Clone to persist (wait, _tempVec3_4 was reused!)
        z_axis: vec3.clone(this._tempVec3_5), // Clone to persist
        R_world_body: R_world_body_corrected
      }
    };
    
    // Fix for y_axis: _tempVec3_4 was reused in transformToBodyFrame.
    // We need to re-compute or save it if we want to return it in body_frame.
    // However, body_frame.y_axis is not strictly used in the current logic except for debugging maybe?
    // Let's recompute it to be safe.
    // y_axis was: leftShoulder - rightShoulder normalized.
    vec3.set(sewCoordinates.body_frame.y_axis,
      leftShoulder.x - rightShoulder.x,
      leftShoulder.y - rightShoulder.y,
      leftShoulder.z - rightShoulder.z
    );
    vec3.normalize(sewCoordinates.body_frame.y_axis, sewCoordinates.body_frame.y_axis);

    return sewCoordinates;
  }

  private _computeWristRotationFromHand(
    handSide: 'left' | 'right',
    poseResults: PoseLandmarkerResult,
    sewPoses: SEWPoses
  ): mat3 | null {
    try {
      if (!poseResults.worldLandmarks || poseResults.worldLandmarks.length === 0) {
        return null;
      }
      
      const landmarks3d = poseResults.worldLandmarks[0];
      const armData = sewPoses[handSide];
      
      // Get wrist position in body-centric coordinates
      const wristPos = armData.W;
      if (!wristPos) return null;
      
      // Get finger landmarks in body-centric frame
      // We need to transform raw landmarks to body frame using _transformToBodyFrame
      // Note: _transformToBodyFrame uses internal temporary vars, which need to be correct.
      // However, _transformToBodyFrame relies on _tempVec3_3 (bodyOrigin) and _tempMat3_2 (R_world_to_body)
      // which were set in _getBodyCentricCoordinates.
      // Since we call this AFTER _getBodyCentricCoordinates, these internal members should still be valid.
      
      const pinkyIdx = handSide === 'left' ? POSE_LANDMARKS.LEFT_PINKY : POSE_LANDMARKS.RIGHT_PINKY;
      const indexIdx = handSide === 'left' ? POSE_LANDMARKS.LEFT_INDEX : POSE_LANDMARKS.RIGHT_INDEX;
      
      // Transform finger landmarks to body-centric coordinates
      // We perform clone here because _transformToBodyFrame reuses a temp vector for output
      // Wait, _transformToBodyFrame creates a NEW vector: vec3.create() and returns it.
      // So no clone needed.
      const pinkyPos = this._transformToBodyFrame(landmarks3d[pinkyIdx]);
      const indexPos = this._transformToBodyFrame(landmarks3d[indexIdx]);
      
      // Compute palm center using body-centric finger positions
      const palmCenter = vec3.create();
      vec3.add(palmCenter, pinkyPos, indexPos);
      vec3.scale(palmCenter, palmCenter, 0.5);

      // X-axis: from wrist towards palm center (in body frame)
      const xAxis = vec3.create();
      vec3.subtract(xAxis, palmCenter, wristPos);
      vec3.normalize(xAxis, xAxis); // gl-matrix handles zero vector? no, acts weird.
      // But wrist to palm shouldn't be zero.

      // Y-axis: perpendicular to the palm plane (in body frame)
      const wristToPinky = vec3.create();
      vec3.subtract(wristToPinky, pinkyPos, wristPos);
      
      const wristToIndex = vec3.create();
      vec3.subtract(wristToIndex, indexPos, wristPos);
      
      // Y-axis is perpendicular to palm plane (cross product in body frame)
      const yAxis = vec3.create();
      vec3.cross(yAxis, wristToIndex, wristToPinky);
      vec3.normalize(yAxis, yAxis);
      
      // Z-axis: Z = X × Y (ensuring right-handed coordinate system in body frame)
      const zAxis = vec3.create();
      vec3.cross(zAxis, xAxis, yAxis);
      vec3.normalize(zAxis, zAxis);

      // Create rotation matrix in body frame: [x_axis, y_axis, z_axis] as columns
      const rotationMatrix = mat3.fromValues(
        xAxis[0], xAxis[1], xAxis[2],
        yAxis[0], yAxis[1], yAxis[2],
        zAxis[0], zAxis[1], zAxis[2]
      );
      
      // Validate that it's a proper rotation matrix using SVD re-orthogonalization?
      // gl-matrix doesn't have SVD. 
      // We can assume normalization and cross products gave us a close-enough orthonormal basis.
      // Or we could do a poor-man's re-orthogonalization if needed (Gram-Schmidt).
      // Given we constructed Z from X and Y, X and Z are orthogonal. X and Y might not be perfectly orthogonal initially?
      // Actually: 
      // X is direction.
      // Y is normal to plane (so Y is orth to X approx? No, only orth to wristToPinky/Index).
      // Let's recompute Y = Z x X to ensure strict orthogonality
      vec3.cross(yAxis, zAxis, xAxis); // Recalculate Y to be strictly orthogonal to X and Z
      vec3.normalize(yAxis, yAxis);

      // reconstruct matrix
      mat3.set(rotationMatrix,
        xAxis[0], xAxis[1], xAxis[2],
        yAxis[0], yAxis[1], yAxis[2],
        zAxis[0], zAxis[1], zAxis[2]
      );

      // gl-matrix is column-major. set(m, m00, m01, m02, m10, m11, m12...)
      // The columns are the basis vectors.
      // m00 = x.x, m01 = x.y, m02 = x.z  <-- Wait.
      // mat3.set takes: m00, m01, m02, m10... 
      // Column 0: [m00, m01, m02] -> X axis
      // Column 1: [m11, m12, m13] -> Wait, indices are:
      // 0 3 6
      // 1 4 7
      // 2 5 8
      // mat3.set args: m00, m01, m02, m10, m11, m12, m20, m21, m22.
      // NO. documentation says: "Sets a mat3 by the given values".
      // Order: m00, m01, m02, m10, m11, m12, m20, m21, m22.
      // If we want columns X, Y, Z:
      // Col 1 is X: X0, X1, X2
      // So m00=X0, m01=X1 (Wait, gl-matrix is column major flattened array [0,1,2,3...]).
      // mat3.set(out, m00, m01, m02 ...) sets the array indices 0, 1, 2...
      // So first 3 args are column 0.
      
      mat3.set(rotationMatrix,
        xAxis[0], xAxis[1], xAxis[2], // Col 0
        yAxis[0], yAxis[1], yAxis[2], // Col 1
        zAxis[0], zAxis[1], zAxis[2]  // Col 2
      );
      
      return rotationMatrix;
            
    } catch (e) {
      if (this.debug) {
        console.error(`Error computing wrist rotation for ${handSide} hand:`, e);
      }
      return null;
    }
  }

  private _checkEngagement(): void {
    // Check if both arms have valid SEW data
    const leftSew = this.humanSewPoses.left;
    const rightSew = this.humanSewPoses.right;

    const leftEngaged = leftSew.S !== null && leftSew.E !== null && leftSew.W !== null;
    const rightEngaged = rightSew.S !== null && rightSew.E !== null && rightSew.W !== null;

    // Require both arms to be visible and tracked for engagement
    this.engaged = leftEngaged && rightEngaged;
  }

  // Public API methods

  startControl(): void {
    this._resetInternalState();
    this.engaged = true;
  }

  getControllerState(): ControllerState {
    return this.controllerState || {
      sew_poses: this._cloneSewPoses(this.humanSewPoses),
      wrist_poses: { left: null, right: null }, // TEMPORARILY DISABLED - hand tracking disabled
      torso_angles: null,
      engaged: this.engaged
    };
  }

  getSewAndWristPoses(): { sew_poses: SEWPoses; wrist_poses: WristPoses } {
    return {
      sew_poses: this._cloneSewPoses(this.humanSewPoses),
      wrist_poses: { left: null, right: null } // TEMPORARILY DISABLED - hand tracking disabled
    };
  }

  getLatestPoseResults(): PoseLandmarkerResult | null {
    return this.latestPoseResults;
  }

  getLatestHandResults(): any | null {
    return null;
  }

  close(): void {
    if (this.poseLandmarker) {
      this.poseLandmarker.close();
      this.poseLandmarker = undefined;
    }
  }

  // Utility methods

  private _cloneSEWPose(pose: SEWPose): SEWPose {
    return {
      S: pose.S ? vec3.clone(pose.S) : null,
      E: pose.E ? vec3.clone(pose.E) : null,
      W: pose.W ? vec3.clone(pose.W) : null
    };
  }

  private _cloneSewPoses(poses: SEWPoses): SEWPoses {
    return {
      left: this._cloneSEWPose(poses.left),
      right: this._cloneSEWPose(poses.right),
      R_world_body: poses.R_world_body ? mat3.clone(poses.R_world_body) : null
    };
  }

  private _cloneWristPoses(poses: WristPoses): WristPoses {
    return {
      left: poses.left ? mat3.clone(poses.left) : null,
      right: poses.right ? mat3.clone(poses.right) : null
    };
  }
}
